import Navigation from "@/components/Navigation";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Heart, Briefcase, Sparkles } from "lucide-react";
import { Helmet } from "react-helmet";
import SchemaMarkup from "@/components/SchemaMarkup";
import Breadcrumb from "@/components/Breadcrumb";
import { createArticleSchema } from "@/utils/schemaHelpers";

const HoroscopoCancer = () => {
  const articleSchema = createArticleSchema(
    "Cáncer: Horóscopo Diario, Características y Compatibilidad con los 12 Signos",
    "Descubre el horóscopo diario, semanal y mensual de Cáncer. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 21 de junio y el 22 de julio.",
    `${window.location.origin}/horoscopo-cancer`
  );

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Inicio",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Horóscopo Cáncer",
        "item": `${window.location.origin}/horoscopo-cancer`
      }
    ]
  };

  const breadcrumbItems = [
    { name: "Inicio", url: "/" },
    { name: "Horóscopo", url: "/horoscopo" },
    { name: "Cáncer" }
  ];

  return (
    <>
      <Helmet>
        <title>Cáncer: Horóscopo Diario, Características y Compatibilidad con los 12 Signos</title>
        <meta name="description" content="Descubre el horóscopo diario, semanal y mensual de Cáncer. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 21 de junio y el 22 de julio." />
        <link rel="canonical" href={`${window.location.origin}/horoscopo-cancer`} />
        <meta property="og:title" content="Cáncer: Horóscopo Diario, Características y Compatibilidad" />
        <meta property="og:description" content="Descubre el horóscopo diario, semanal y mensual de Cáncer. Predicciones de amor, trabajo, salud y dinero." />
        <meta property="og:url" content={`${window.location.origin}/horoscopo-cancer`} />
        <meta property="og:type" content="article" />
      </Helmet>
      
      <SchemaMarkup schema={articleSchema} />
      <SchemaMarkup schema={breadcrumbSchema} />
      
      <div className="min-h-screen">
        <Navigation />
        
        <main className="pt-24 pb-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <Breadcrumb items={breadcrumbItems} />
            
            <div className="text-center mb-12">
              <div className="text-6xl mb-4">♋</div>
              <h1 className="text-2xl font-bold mb-4 text-glow">
                Horóscopo de Cáncer
              </h1>
              <p className="text-xl text-muted-foreground">21 de Junio - 22 de Julio</p>
            </div>

            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-primary">Tu Horóscopo Cáncer: Predicciones Actualizadas</h2>
              
              <Card className="p-8 gradient-celestial">
                <Tabs defaultValue="hoy" className="w-full">
                  <TabsList className="grid w-full grid-cols-3 mb-8">
                    <TabsTrigger value="hoy">Hoy</TabsTrigger>
                    <TabsTrigger value="semanal">Semanal</TabsTrigger>
                    <TabsTrigger value="mensual">Mensual</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="hoy" className="space-y-6">
                    <article data-zodiac-sign="cancer" data-prediction-type="daily" data-date={new Date().toISOString().split('T')[0]}>
                      <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                        <Heart className="w-6 h-6 text-primary" />
                        Horóscopo de Hoy para Cáncer
                        <time dateTime={new Date().toISOString().split('T')[0]} className="sr-only">
                          {new Date().toLocaleDateString('es-ES')}
                        </time>
                      </h3>
                      <p className="text-center text-sm text-muted-foreground mb-6">Número: 2 | Color: Plateado</p>
                      <div className="space-y-4">
                        <div data-category="love">
                          <h4 className="text-base font-semibold mb-2 text-primary">💖 Amor</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            La Luna exalta tu sensibilidad e instinto protector. Es un día para fomentar la intimidad emocional y la seguridad en tu hogar. Tu capacidad de nutrir será muy apreciada.
                          </p>
                        </div>
                        <div data-category="work">
                          <h4 className="text-base font-semibold mb-2 text-secondary">💼 Trabajo</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            Confía en tu intuición al manejar asuntos profesionales. Las tareas relacionadas con el hogar o el cuidado se ven favorecidas. Busca la estabilidad en tu entorno laboral.
                          </p>
                        </div>
                        <div data-category="health">
                          <h4 className="text-base font-semibold mb-2 text-accent">✨ Salud</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            El descanso emocional es primordial. Evita las situaciones estresantes y dedica tiempo a actividades que te hagan sentir seguro/a. Cuida tu estómago.
                          </p>
                        </div>
                        <div data-category="money">
                          <h4 className="text-base font-semibold mb-2 text-accent">💰 Dinero</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            Los asuntos relacionados con el hogar o la familia pueden tener implicaciones financieras positivas. Es un buen momento para invertir en bienes raíces. Tu instinto te guiará hacia la seguridad económica.
                          </p>
                        </div>
                      </div>
                    </article>
                  </TabsContent>
                  
                  <TabsContent value="semanal" className="space-y-6">
                    <div>
                      <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                        <Briefcase className="w-6 h-6 text-secondary" />
                        Horóscopo Semanal: Tu Guía para los Próximos 7 Días
                      </h3>
                      <p className="text-muted-foreground leading-relaxed mb-4">
                        Esta semana enfatiza la importancia del hogar y la familia. Los primeros días favorecen conversaciones emocionales importantes y la resolución de asuntos del pasado. El fin de semana es ideal para reuniones familiares y crear nuevos recuerdos.
                      </p>
                      <p className="text-muted-foreground leading-relaxed">
                        La Luna en sus diferentes fases te afecta particularmente. Sigue sus ciclos para optimizar tu energía. Tus instintos protectores estarán activos, permitiéndote cuidar de quienes amas de manera especial. Confía en tu intuición.
                      </p>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="mensual" className="space-y-6">
                    <div>
                      <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                        <Sparkles className="w-6 h-6 text-accent" />
                        Horóscopo Mensual Cáncer: Enfoque y Oportunidades Clave
                      </h3>
                      <p className="text-muted-foreground leading-relaxed mb-4">
                        Este mes trae oportunidades para fortalecer tus raíces y conexiones emocionales. La primera mitad favorece proyectos relacionados con el hogar, la familia y tu seguridad emocional. Inversiones en bienes raíces o mejoras del hogar son especialmente favorables.
                      </p>
                      <p className="text-muted-foreground leading-relaxed">
                        La segunda mitad del mes es ideal para sanar heridas emocionales del pasado y establecer límites saludables. Tu capacidad natural para el cuidado será reconocida. Es momento de nutrir tus sueños mientras mantienes los pies en la tierra.
                      </p>
                    </div>
                  </TabsContent>
                </Tabs>
              </Card>
            </section>

            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-secondary">Características Profundas del Signo Cáncer: La Madre Nutricia</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Los Pilares Astrológicos de Cáncer</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-bold text-base mb-2 text-primary">💧 Elemento: Agua</h4>
                    <p className="text-muted-foreground">Enfoque en las emociones, la intuición y la sensibilidad. Cáncer fluye con las mareas emocionales.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-secondary">🌊 Modalidad: Cardinal</h4>
                    <p className="text-muted-foreground">Define la necesidad de proteger, iniciar y cuidar. Líder emocional y guardián del hogar.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-accent">🌙 Astro Regente: La Luna</h4>
                    <p className="text-muted-foreground">Símbolo de la emoción, el hogar y el inconsciente. Rige los ciclos emocionales y la memoria.</p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Perfil de Personalidad Cáncer: Fortalezas y Debilidades</h3>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-bold text-base mb-3 text-primary">✨ Fortalezas</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Empatía:</strong> Comprensión profunda de las emociones ajenas</li>
                      <li>• <strong>Protección:</strong> Guardián natural de sus seres queridos</li>
                      <li>• <strong>Intuición:</strong> Percepción psíquica altamente desarrollada</li>
                      <li>• <strong>Memoria:</strong> Recuerda detalles emocionales con claridad</li>
                      <li>• <strong>Devoción:</strong> Lealtad inquebrantable</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-3 text-secondary">⚠️ Debilidades</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Hipersensibilidad:</strong> Se hiere fácilmente</li>
                      <li>• <strong>Manipulación:</strong> Puede usar emociones para controlar</li>
                      <li>• <strong>Inseguridad:</strong> Necesita constante reafirmación</li>
                      <li>• <strong>Aferramiento al pasado:</strong> Dificultad para soltar</li>
                      <li>• <strong>Defensividad:</strong> Se retrae en su caparazón</li>
                    </ul>
                  </div>
                </div>
                <div className="text-center p-4 bg-primary/10 rounded-lg">
                  <p className="text-lg font-bold text-primary">Mantra Clave: "Yo Siento"</p>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Cáncer en el Amor: Familia, Hogar y Profundidad Emocional</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Para Cáncer, el amor es el núcleo de su existencia. Busca crear un hogar seguro donde pueda nutrir y ser nutrido. Su amor es profundo, devoto y protector. Valora la tradición, la familia y los rituales compartidos que crean lazos emocionales duraderos.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Una vez comprometido, Cáncer es increíblemente leal y cuidadoso. Necesita una pareja que valore la intimidad emocional y esté dispuesta a crear un santuario compartido. Su sensibilidad requiere un compañero comprensivo que respete sus cambios emocionales y no lo juzgue por su necesidad ocasional de retirarse a su espacio seguro.
                </p>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Cáncer en el Trabajo y Finanzas: Emocionalidad en los Negocios</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Cáncer destaca en carreras de cuidado: enfermería, trabajo social, psicología, educación infantil, gastronomía y negocios relacionados con el hogar. Su intuición lo hace excelente en bienes raíces, historia y cualquier campo que requiera memoria emocional y conexión con el pasado.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Con las finanzas, Cáncer es conservador y orientado a la seguridad. Ahorra para el futuro y tiende a invertir en bienes tangibles, especialmente propiedades. Valora la estabilidad económica como base para la seguridad emocional de su familia. Excelente para construir patrimonio familiar a largo plazo.
                </p>
              </Card>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-6 text-accent">Compatibilidad de Cáncer con los Otros Signos Zodiacales</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">💚 Tu Match Perfecto</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-base mb-2">♏ Escorpio y ♓ Piscis</h4>
                    <p className="text-muted-foreground">
                      Los signos de agua comparten profundidad emocional e intuición. Con Escorpio hay intensidad y lealtad mutua. Con Piscis, una conexión espiritual y empática que trasciende las palabras.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2">♉ Tauro y ♍ Virgo</h4>
                    <p className="text-muted-foreground">
                      Los signos de tierra proporcionan la estabilidad que Cáncer necesita. Tauro ofrece seguridad y sensualidad, mientras que Virgo aporta servicio dedicado y atención a los detalles del cuidado diario.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">⚡ Los Desafíos y Lecciones de Cáncer</h3>
                <p className="text-muted-foreground mb-4">
                  La separación y la objetividad son los grandes desafíos de Cáncer. Los signos de aire (Géminis, Libra, Acuario) lo empujan a racionalizar sus emociones y ver las cosas desde una perspectiva más desapegada.
                </p>
                <p className="text-muted-foreground">
                  Capricornio, su signo opuesto, le enseña sobre estructura emocional, límites saludables y el equilibrio entre vida personal y profesional. Los signos de fuego (Aries, Leo, Sagitario) desafían su tendencia a aferrarse, enseñándole sobre independencia y aventura emocional.
                </p>
              </Card>
            </section>
          </div>
        </main>
      </div>
    </>
  );
};

export default HoroscopoCancer;